import { useEffect, useRef, useState } from "react";
import { MessageSquare, Check, X, Trash2, StickyNote } from "lucide-react";

// Mock versions of the existing Connexions / Messages / Commandes / Notes tabs for the Template D preview.
const now = () => new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
const TL = [
  { at: "aujourd'hui @ 16:35", dot: "bg-green-500 animate-pulse", t: <b>En ligne maintenant <span className="ml-1 rounded-full bg-slate-600 text-white text-[11px] px-2 py-0.5 font-normal">8h 23min</span></b> },
  { at: "aujourd'hui @ 16:35", dot: "bg-sky-600", t: <span><b>Frédéric</b> a pris la main · en cours</span> },
  { at: "aujourd'hui @ 14:02", dot: "bg-slate-300", t: <span>Frédéric : <code className="text-[12px] bg-slate-100 px-1 rounded">ipconfig /flushdns</code></span> },
  { at: "aujourd'hui @ 10:12", dot: "bg-sky-600", t: <span><b>Frédéric</b> a pris la main · 14 min</span> },
  { at: "aujourd'hui @ 08:12", dot: "bg-green-500", t: <b>Agent connecté · Sophie</b> },
  { at: "hier @ 19:04", dot: "bg-slate-400", t: <b>Hors ligne <span className="ml-1 rounded-full bg-slate-600 text-white text-[11px] px-2 py-0.5 font-normal">10h 52min</span></b> },
  { at: "hier @ 08:09", dot: "bg-green-500", t: <b>Agent connecté · Sophie</b> },
];
export const DConnections = () => (
  <div className="p-4 max-h-[340px] overflow-y-auto" data-testid="d-connections">
    <ul className="relative border-l-2 border-green-200 ml-2">
      {TL.map((r, i) => (
        <li key={i} className="relative pl-6 py-1.5 text-[13px]">
          <span className={`absolute left-[3px] top-3 h-2.5 w-2.5 rounded-full ${r.dot}`} />
          <p className="text-slate-400 text-[11px]">{r.at}</p><p className="text-slate-800">{r.t}</p>
        </li>
      ))}
    </ul>
  </div>
);

const SendBar = ({ id, placeholder, label, onSend, disabled, mono }) => {
  const [v, setV] = useState("");
  const go = () => { if (v.trim()) { onSend(v.trim()); setV(""); } };
  return (
    <div className="flex items-center gap-2 border rounded-full bg-white pl-4 pr-1 py-1 mt-3">
      <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()} disabled={disabled} data-testid={`d-${id}-input`}
        placeholder={disabled ? "Poste hors ligne" : placeholder} className={`flex-1 min-w-0 outline-none text-sm bg-transparent ${mono ? "font-mono" : ""}`} />
      <button onClick={go} disabled={disabled || !v.trim()} data-testid={`d-${id}-send`} className="rounded-full bg-sky-600 text-white text-sm font-semibold px-4 h-8 disabled:bg-slate-300">{label}</button>
    </div>
  );
};
const useEnd = (dep) => { const r = useRef(null); useEffect(() => { if (r.current) r.current.scrollTop = r.current.scrollHeight; }, [dep]); return r; };

export const DChat = ({ on }) => {
  const [msgs, setMsgs] = useState([
    { me: false, name: "Sophie", at: "09:41", text: "Bonjour, l'imprimante ne répond plus depuis ce matin." },
    { me: true, name: "Frédéric", at: "09:43", text: "Bonjour Sophie, je regarde tout de suite." },
  ]);
  const ref = useEnd(msgs.length);
  const send = (text) => setMsgs((m) => [...m, { me: true, name: "Frédéric", at: now(), text }]);
  return (
    <div className="p-4 flex flex-col h-[340px]" data-testid="d-chat">
      <div ref={ref} className="flex-1 overflow-y-auto space-y-2">
        {msgs.map((x, i) => (
          <div key={i} className={`text-sm rounded-lg px-3 py-2 max-w-[85%] ${x.me ? "bg-sky-50 ml-auto" : "bg-slate-100"}`}>
            <span className="font-semibold">{x.name}</span> <span className="text-xs text-slate-500">{x.at}</span><p className="whitespace-pre-wrap">{x.text}</p>
          </div>
        ))}
        {!msgs.length && <div className="text-center text-slate-500 pt-10"><MessageSquare className="h-10 w-10 mx-auto text-slate-300 mb-2" />Aucun message échangé pour le moment</div>}
      </div>
      <SendBar id="chat" placeholder="Saisir un message (s'affiche sur le poste)" label="Envoyer" onSend={send} disabled={!on} />
    </div>
  );
};

export const DCommands = ({ on }) => {
  const [shell, setShell] = useState("cmd");
  const [rows, setRows] = useState([{ id: 1, at: "14:02", cmd: "ipconfig /flushdns", ok: true, out: "Configuration IP de Windows\nLe cache de résolution DNS a été vidé." }]);
  const ref = useEnd(rows.length);
  const run = (cmd) => {
    const id = Date.now();
    setRows((r) => [...r, { id, at: now(), cmd, pending: true }]);
    setTimeout(() => setRows((r) => r.map((x) => (x.id === id ? { ...x, pending: false, ok: true, out: `(${shell === "cmd" ? "Invite de commandes" : "PowerShell"}) commande exécutée — exemple de sortie` } : x))), 900);
  };
  return (
    <div className="p-4 flex flex-col h-[340px]" data-testid="d-commands">
      <div ref={ref} className="flex-1 overflow-y-auto divide-y border rounded-md bg-white">
        {rows.map((r) => (
          <div key={r.id} className="p-3 font-mono text-[12px]">
            <div className="flex items-center gap-2 font-sans"><b>Frédéric</b><span className="text-slate-500">{r.at}</span>
              {r.pending ? <span className="text-slate-400">exécution…</span> : r.ok ? <Check className="h-4 w-4 text-emerald-600" /> : <X className="h-4 w-4 text-red-500" />}
              {!r.pending && <button onClick={() => setRows((L) => L.filter((x) => x.id !== r.id))} className="ml-auto text-slate-400 hover:text-red-600" data-testid={`d-cmd-del-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
            </div>
            <p className="mt-1 text-slate-800 break-all">&gt; {r.cmd}</p>
            {r.out && <pre className="mt-1 whitespace-pre-wrap text-slate-600">{r.out}</pre>}
          </div>
        ))}
        {!rows.length && <p className="p-6 text-center text-sm text-slate-500">Aucune commande. Elles s'exécutent avec les droits administrateur, sans prise en main.</p>}
      </div>
      <div className="flex gap-4 text-xs mt-2 text-slate-600">
        {[["cmd", "Invite de commandes"], ["powershell", "PowerShell"]].map(([k, l]) => (
          <label key={k} className="flex items-center gap-1"><input type="radio" checked={shell === k} onChange={() => setShell(k)} data-testid={`d-shell-${k}`} />{l}</label>
        ))}
      </div>
      <SendBar id="cmd" mono placeholder="Saisir une commande" label="Exécuter" onSend={run} disabled={!on} />
    </div>
  );
};

export const DNotes = () => {
  const [notes, setNotes] = useState([{ at: "22/10/2026", by: "Frédéric", text: "Imprimante Brother réinstallée, pilote v4.2." }]);
  const [v, setV] = useState("");
  return (
    <div className="p-4 space-y-2 max-h-[340px] overflow-y-auto" data-testid="d-notes">
      {notes.map((n, i) => (
        <div key={i} className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-[13px]"><p className="text-[11px] text-slate-500 flex items-center gap-1"><StickyNote className="h-3 w-3" />{n.by} · {n.at}</p><p>{n.text}</p></div>
      ))}
      <textarea value={v} onChange={(e) => setV(e.target.value)} rows={2} placeholder="Ajouter une note sur ce poste…" data-testid="d-note-input" className="w-full rounded-md border border-slate-200 p-2 text-sm outline-none focus:border-sky-500" />
      <button disabled={!v.trim()} onClick={() => { setNotes((L) => [...L, { at: new Date().toLocaleDateString("fr-FR"), by: "Frédéric", text: v.trim() }]); setV(""); }}
        data-testid="d-note-add" className="rounded-md bg-sky-600 text-white text-[13px] font-semibold px-3 h-8 disabled:bg-slate-300">Ajouter la note</button>
    </div>
  );
};
